import {
  estEtapeClose,
  type EtapeDossier,
  CHIP_CLIENT,
  isPaymentMean,
  PAYMENT_MEAN_LABELS,
} from "@hakili/domain-types";
import type { ClientOfferTerms } from "@hakili/contracts";
import { formatDate, formatDateTime, formatMoney, pl, t } from "@hakili/i18n";
import { CheckCircle2, Clock } from "lucide-react";
import { useState } from "react";

import { type DossierView, type FileFeeView, labelWithYear } from "./dossier.js";
import type { VoucherView } from "./retrait.js";

/**
 * `round CW`, `CW.5`: **« Où en est ma demande », the drawing's fourth tab.**
 *
 * Where the buyer's demand stands, as against « Mon dossier », which is his
 * pieces. The drawing gives the second its own tab because it is the one a
 * buyer opens every day while he waits.
 *
 * ## Three things and no fourth
 *
 * The card that says what is happening and whether anything is asked of him,
 * the path his file has travelled, and , where a door exists , the one gesture
 * that is his to make. Everything else the drawing shows on this screen (the
 * expert's report, the offers, the guarantee decision, the withdrawal note)
 * belongs to doors no build has yet, and `A48` is explicit: a state no door
 * reaches stays drawing-only rather than being mocked here.
 *
 * ## The path is the API's, and it stops where the file is
 *
 * `I20`, and `cheminStepper` implements it: **the stages travelled, plus the
 * terminal branch, and zero future nodes.** The drawing draws the remaining
 * milestones in grey ahead of the current one; the invariant says a file must
 * never be shown a stage it may not reach. Both texts are kept and the
 * divergence is reported for ratification rather than settled here , an
 * invariant is not amended by a screen.
 *
 * ## Not one sentence of this screen is chosen by the screen
 *
 * The stage's title, the chip and its tone all come from the door. The chip
 * above all: it used to be the CONSOLE's, so a file at `intake` told the buyer
 * « À vous de jouer » while Hakili was doing the work. `chipClient` is the
 * cure and it lives in `domain-types`, so the six frontends cannot disagree
 * about who is being waited on.
 */

/** The tones the door can send, and the drawing's own colours for them. */
const TONES: Readonly<Record<string, { readonly box: string; readonly text: string }>> = {
  vous: { box: "border-indigo-300 bg-indigo-50", text: "text-indigo-700" },
  termine: { box: "border-emerald-200 bg-emerald-50", text: "text-emerald-700" },
  attente: { box: "border-gray-200 bg-gray-50", text: "text-gray-600" },
  clos: { box: "border-gray-200 bg-gray-50", text: "text-gray-600" },
};

/**
 * The buyer's sentence for each stage, keyed by the STAGE ID.
 *
 * A declared map to literal keys and never a template , `P1-CLW-033`'s own
 * rule, « a label is not a key »: a key assembled from a stage id would be
 * invisible to the catalogue's guard, and a stage this build has not heard of
 * would render its own wire word to a buyer.
 *
 * **They are new copy and the addendum says so.** The drawing has sixteen
 * states in its own vocabulary; the record has thirteen stages in 2.4.1's. The
 * sentences below are the drawing's meaning carried onto the record's stages,
 * with every FIGURE removed , no count of banks, no delay in hours, no date ,
 * because not one of those is answered by a door, and a screen that printed
 * them would be promising on the product's behalf.
 */
const SENTENCES = new Map<string, string>([
  ["intake", "client.suivi.etapeIntake"],
  ["collecte", "client.suivi.etapeCollecte"],
  ["expertise", "client.suivi.etapeExpertise"],
  ["analyse", "client.suivi.etapeAnalyse"],
  ["pret", "client.suivi.etapePret"],
  ["soumis", "client.suivi.etapeSoumis"],
  ["offre", "client.suivi.etapeOffre"],
  ["assurance", "client.suivi.etapeAssurance"],
  ["conditions", "client.suivi.etapeConditions"],
  ["decaisse", "client.suivi.etapeDecaisse"],
  ["refuse", "client.suivi.etapeRefuse"],
  ["refuseBanque", "client.suivi.etapeRefuseBanque"],
  ["sansSuite", "client.suivi.etapeSansSuite"],
]);

/**
 * `FX-1`, `M2`: the closure's MOTIVE, in the buyer's own words.
 *
 * The founder's rule of 24 September, entered in `03` at `WF-CLI-16`, asks « Suivi » to keep the
 * last closed dossier readable with its final stage, **the closure's motive** and its date. The
 * code is `02` \u00a72.4.1's `dossier.closure_code`; the screen names it rather than printing an enum
 * at the buyer, and says nothing at all where the door answers no code (`R81`).
 */
const MOTIF_CLIENT: ReadonlyMap<string, string> = new Map([
  ["clientChangeAvis", "client.suivi.closMotifClientChangeAvis"],
  ["clientInjoignable", "client.suivi.closMotifClientInjoignable"],
  ["vehiculeAcheteAilleurs", "client.suivi.closMotifVehiculeAcheteAilleurs"],
  ["financementObtenuAilleurs", "client.suivi.closMotifFinancementObtenuAilleurs"],
  ["autre", "client.suivi.closMotifAutre"],
]);

/**
 * The one gesture that is the buyer's, where a door carries it.
 *
 * `collecte` alone today: his pieces live behind « Mon dossier ». Every other
 * stage the drawing gives a button to , paying the fee, reading the report,
 * choosing an offer , waits on a door, and a button that led to a marked stub
 * would be the dead affordance `R81` abolished.
 */
/**
 * `round C3-A`, `C3-A.2`: the two gestures `intake` can carry, keyed by what the
 * door derived , never by the stage, which is the same for both.
 *
 * « Lancer les vérifications » goes to `/suivi` because it is not a screen: it
 * is the buyer's own door, pressed from the tracking card itself. The round
 * that wires it is `C3-A.3`, beside the means; until then the word is right and
 * the destination is the card he is already on.
 */
/**
 * `round C3-H`: **a control existing is not a control that calls.** The
 * founder's finding on staging, 12 September, main `5d1ab7bb`: « Lancer les
 * vérifications » fired no request , its path was `/suivi`, the card he was
 * on. The launch is a DOOR, not a destination: `launch` names no path and the
 * card calls `props.lancer` with the file's id, then re-reads what « Suivi »
 * reads. « Régler les frais » stays a destination, the payment screen.
 */
const GESTES: Readonly<Record<string, { readonly key: string; readonly path: string | null } | undefined>> = {
  fee: { key: "client.suivi.reglerFrais", path: "/paiement" },
  launch: { key: "client.suivi.lancerVerifications", path: null },
};

/**
 * The founder's second finding of 12 September: under the offered launch the
 * card still read « Les frais de vérification sont à régler … ». The sentence
 * follows the fee's state as the control does , the drawing's own words for a
 * received fee, « Vous décidez du départ » (`hakili-client.jsx`, the payment
 * confirmation), carried onto the record's `intake`.
 */
const SENTENCES_BY_GESTE = new Map<string, string>([["launch", "client.suivi.etapeIntakeLance"]]);

const ACTIONS = new Map<string, { readonly key: string; readonly path: string }>([
  ["collecte", { key: "client.suivi.completer", path: "/dossier" }],
]);

/** A template's holes filled; the key is a literal so the catalogue's guard sees it. */
const DASH = t("common.valeur.tiret");

const fill = (key: Parameters<typeof t>[0], values: Readonly<Record<string, string>>): string =>
  Object.entries(values).reduce((text, [hole, value]) => text.split(`{${hole}}`).join(value), t(key));

export interface SuiviDossier {
  /** `C3-H`: the file's id, which the launch door takes. */
  readonly id: string;
  /**
   * `round W-13`, `M1`: **the verdict the door answered, and the figures the
   * card argues with.** The card derives its state from the verdict first and
   * from the stage otherwise; a verdict the stage has not caught up with is
   * still the buyer's fact.
   */
  readonly verdict: "conforme" | "ecartprix" | "nonconforme" | null;
  readonly report: {
    readonly estimatedValueAmount: number;
    /** The price that stands now (the seller's answer to the gap moves it). */
    readonly askedPriceAmount: number;
    readonly gapPct: number | null;
    /** `PRICE-GAP-DEALER`, M2: the highest price that conforms (value x 1,10); the restart threshold. */
    readonly ceilingAmount?: number;
    /** The seller's answer to the gap, on the platform; `null` while he has not answered. */
    readonly priceAnswer?: "aligne" | "propose" | "maintenu" | "accepte" | null;
  } | null;
  readonly feeClientAmount: number | null;
  readonly refund: { readonly reason: string; readonly orderedOn: string } | null;
  /**
   * `round K-2` (resumed), `M4` (WF-CLI-16's neighbour): the closure's own code and, on the seller's
   * breach, the refund at the dealer's charge , the total, and the day Hakili confirmed it.
   */
  readonly closure: {
    readonly code: string | null;
    readonly reason: string | null;
    /** `FX-1b`, `M2`: the day the file closed , `dossier.dossiers.closed_at`, on every closed stage. */
    readonly at: string | null;
  } | null;
  readonly breachRefund: { readonly totalAmount: number; readonly confirmedOn: string | null } | null;
  /** `T-2b` (`P2-PAY-037`): the file fee ordered back when the file ended without the bank's funds. */
  readonly fileFeeRefund?: { readonly totalAmount: number; readonly confirmedOn: string | null } | null;
  /** The seller's business number, or `null` when no door answered it , a dash then, never a guess. */
  readonly sellerPhone: string | null;
  readonly controlPrevented: {
    readonly reason: string;
    readonly detail: string | null;
    readonly at: string;
  } | null;
  /** `L-1b`, `M5` (c): the offers the banks sent, counted by the door , the drawing's « N offres reçues » card. */
  readonly offersReceived?: number;
  /** Round `L-2`, `M4` (A8): the offer the buyer retained, the door's; `null` until he accepts. */
  readonly retainedOffer?: {
    readonly offerId: string;
    readonly bankName: string;
    readonly circuit: "comite" | "signature" | null;
    readonly amountWarranty: number;
    readonly financesWarranty: boolean;
    /** `CLIENT-OFFER-TERMS` (D-1102-3): the four terms of this offer as the door serves them; absent or `null` on an older answer. */
    readonly terms?: ClientOfferTerms | null;
    /**
     * Round `T-1`, `M3` (WF-CLI-24 steps 4 and 5): the deposit at the accepted offer's REAL
     * amount, the two fees each with its financing, the bank's signature as the door carries
     * it , the pack, the appointment, the documents' names and states; no scan id.
     */
    readonly depositAmount: number | null;
    /** `T-1b`: the loan's vehicle part, 05 §2.6.13's `amount_vehicle` , « Montant financé pour le véhicule » on the money's circuit. */
    readonly amountVehicle: number;
    readonly feeBankAmount: number;
    readonly feeHakiliAmount: number;
    readonly financesBankFee: boolean;
    readonly financesHakiliFee: boolean;
    readonly signature: {
      readonly packSentAt: string | null;
      readonly contractSignedAt: string | null;
      readonly appointment: { readonly agence: string; readonly when: string } | null;
      readonly documents: readonly {
        readonly code: string;
        readonly label: string;
        readonly pageCount: number;
        readonly state: string;
      }[];
      readonly signedCount: number;
    } | null;
  } | null;
  /** Round `L-2`, `M5` (WF-CLI-23): the buyer's decision on an unfinanced warranty; `null` before. */
  readonly warrantySettlement?: "comptant" | "retiree" | null;
  /**
   * Round `T-1b`, `M3` (WF-CLI-26): what remains before the money , the retained bank's
   * conditions as facts and Hakili's fee condition (`null` while no fact exists, T-1c's) ,
   * and how the money circulates, the two transfers on the sale price by the door's quote.
   */
  readonly conditions?: {
    readonly bank: {
      readonly bankName: string;
      readonly conditions: readonly {
        readonly key: string;
        readonly label: string;
        readonly state: string;
        readonly liftedAt: string | null;
      }[];
      readonly liftedCount: number;
      readonly total: number;
    } | null;
    readonly hakiliFeeSettled: boolean | null;
    readonly hakiliFeeFinanced: boolean;
  } | null;
  /**
   * `round K-1`, `M3 (d)`: the days the pending move has been HIS on a held file, from the
   * server's own ledger; `null` while the move is not his. One plain sentence, nothing else.
   */
  readonly waitingOnYouDays?: number | null;
  readonly moneyFlow?: {
    readonly salePriceAmount: number;
    readonly ratePct: number;
    readonly dealerAmount: number;
    readonly hakiliAmount: number;
  } | null;
  /** Round `L-3`, `M2` (WF-CLI-20): the study in aggregate and the one request, the view's own. */
  readonly study?: DossierView["study"];
  readonly completionRequest?: DossierView["completionRequest"];
  readonly intention?: string;
  readonly vehicleYear?: number | null;
  readonly sellerName: string;
  /**
   * `round C3-A`, `C3-A.2`: **what the buyer owes, derived by the DOOR.**
   *
   * `intake` is his stage now, and what he owes there changes without the stage
   * moving: the FEE until the client half is received, the LAUNCH once it is.
   * The door decides the word because deciding it here would teach a screen to
   * read a payment state, and `02`'s `DECISION ADDED 2` keeps it derived rather
   * than stored, so a webhook landing a second ago is already true.
   */
  readonly geste: "fee" | "launch" | null;
  /** `P1-DOS-010`: the chargé the birth chose , WF-CLI-19 names him at `analyse`. */
  readonly ownerName?: string | null;
  readonly reference: string;
  readonly vehicleLabel: string;
  readonly etape: string;
  readonly stage: string;
  readonly attente: string;
  readonly ton: string;
  readonly chemin: readonly { readonly id: string; readonly label: string }[];
}

export interface SuiviProps {
  readonly dossier: SuiviDossier | null;
  readonly naviguer: (path: string) => void;
  /** Round `PAY-P`, `M3`: the coverage door's answer for the file's car; `null` before it answered. */
  readonly coverage?: {
    readonly city: string | null;
    readonly covered: boolean;
    readonly cabinetName: string | null;
    readonly nextControlDay: { readonly label: string; readonly dayOn: string } | null;
  } | null;
  /** `C3-H`: the buyer's launch door, `POST /me/dossiers/{id}/launch-verification` with his token. */
  readonly lancer?: (dossierId: string) => Promise<void>;
  /** `C3-H`: re-read what « Suivi » reads, once the door has answered. */
  readonly relire?: () => Promise<void>;
  /** `W-13`: the buyer's abandon door, `POST /me/dossiers/{id}/abandon` with his token (WF-CLI-16). */
  readonly abandonner?: (dossierId: string) => Promise<void>;
  /** `PRICE-GAP-DEALER`, M2: « J'accepte ce prix », `POST /me/dossiers/{id}/accept-price` with his token. */
  readonly acceptProposedPrice?: (dossierId: string) => Promise<void>;
  /**
   * Round `C6`, `M4` (`OD-31`, `OD-33`), WF-CLI-14 step 2: « Voir le rapport »
   * , the cabinet's own document and Hakili's derived sheet for the current
   * version, `GET /me/dossiers/{id}/rapport` with his token. Neither carries
   * the asked price or the verdict; the gap stays on the card, above them.
   */
  readonly lireRapport?: (dossierId: string) => Promise<ReportReading | null>;
  /** Round `L-2`, `M5` (WF-CLI-23): the buyer's decision on an unfinanced warranty. */
  readonly trancherGarantie?: (dossierId: string, decision: "comptant" | "retiree") => Promise<void>;
  /**
   * Round `L-3b`, `M3` (`P1-CLW-022`, WF-CLI-21 step 4): « Voir des véhicules
   * dans votre capacité » on a terminal file , the app's active dossier
   * cleared and the search shown; the one-live-file guard is the door's
   * (`P1-DOS-034`, I25), which reads the stage when a new file opens.
   */
  readonly liberer?: () => void;
  /** `T-1c`, `M2` (WF-CLI-24 step 5): his file fee by his own door; `null` while none is born. */
  readonly fileFee?: FileFeeView | null;
  /** `T-2` (WF-CLI-27): the settlement's two legs and the bon, or the block, on the file from the bank's ordre. */
  readonly voucher?: VoucherView | null;
  /** `T-2b`, `M2` (WF-CLI-13 option 2): « Acheter comptant, sans crédit » opens the cash settlement by the buyer's own door. */
  readonly acheterComptant?: (dossierId: string) => Promise<void>;
  /** `T-2b`: a cash settlement stands on the file (the buyer took his option). */
  readonly comptantOuvert?: boolean;
  /** `T-2c`, `M2 (4)`: the keys' instant on the cash path , WF-CLI-28 step 4's end, the dossier released. */
  readonly comptantRemisLe?: string | null;
  /** `T-1c`: « J'ai réglé les frais de dossier » , the means used and his reference, by his own door. */
  readonly declareFileFee?: (dossierId: string, means: string, reference: string) => Promise<void>;
}

export interface ReportReading {
  readonly cabinetName: string;
  readonly version: number;
  readonly current: {
    readonly version: number;
    readonly document: { readonly contentHash: string; readonly url: string } | null;
    readonly sheet: { readonly contentHash: string; readonly url: string } | null;
  };
}

/**
 * `CLIENT-OFFER-TERMS` (D-1102-3): the rate as the BROWSER formats the served `nominalRatePct` (`fr-FR`, one decimal: « 10,8 »), then the sign after U+202F, the narrow no-break space (`PCT-SPACE`,
 * D-1025-AA-1). The sign's space is written here and not left to the engine's percent style: the engines disagree on which no-break space follows a figure, and the record says U+202F.
 */
const RATE_FORMAT = new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/** The four rows, in the served order, under the SERVED labels; every value is a served figure formatted, none is computed. */
const termRows = (
  terms: ClientOfferTerms,
): readonly { readonly key: string; readonly label: string; readonly value: string }[] => [
  { key: "amount", label: terms.labels.amountFinanced, value: formatMoney(terms.amountFinancedAmount) },
  {
    key: "duration",
    label: terms.labels.duration,
    value: pl(terms.durationMonths, t("client.offres.mois"), t("client.offres.mois")),
  },
  {
    key: "rate",
    label: terms.labels.nominalRate,
    value: `${RATE_FORMAT.format(terms.nominalRatePct)}\u202f%`,
  },
  { key: "instalment", label: terms.labels.instalment, value: formatMoney(terms.instalmentAmount) },
];

export function Suivi(props: SuiviProps): React.JSX.Element {
  const dossier = props.dossier;
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [negotiating, setNegotiating] = useState(false);
  // `T-1`, `M3`: « Voir les documents à signer » , a hook before every early return, as the others.
  const [documentsOpen, setDocumentsOpen] = useState(false);
  // `T-1c`, `M2`: the fee's declaration form , the means chosen, the reference typed, the door's refusal.
  const [feeForm, setFeeForm] = useState<{
    open: boolean;
    means: string;
    reference: string;
    word: string | null;
  }>({
    open: false,
    means: "",
    reference: "",
    word: null,
  });
  // Round `C6`, `M4`: the report of record, read on the buyer's own gesture.
  const [reading, setReading] = useState<{
    open: boolean;
    report: ReportReading | null;
    word: string | null;
  }>({
    open: false,
    report: null,
    word: null,
  });
  if (dossier === null) {
    return (
      <div className="px-5 pt-5" data-testid="suivi-aucun">
        <h1 className="text-xl font-semibold tracking-tight text-gray-900">{t("client.suivi.titre")}</h1>
        <p className="mt-1 text-sm text-gray-500">{t("client.dossier.aucun")}</p>
      </div>
    );
  }

  const tone = TONES[dossier.ton] ?? TONES["attente"];
  const datedState =
    (dossier.stage === "intake" && dossier.geste === "launch") || dossier.stage === "expertise";
  const coverage = props.coverage ?? null;
  const controlDate =
    !datedState || coverage === null || !!dossier.controlPrevented
      ? null
      : coverage.nextControlDay === null
        ? fill("client.suivi.controleSansJeudi", { city: coverage.city ?? DASH })
        : fill("client.suivi.controleLe", { day: coverage.nextControlDay.label });
  const sentence = SENTENCES_BY_GESTE.get(dossier.geste ?? "") ?? SENTENCES.get(dossier.stage);
  /**
   * `L-1b`, `M5` (c), WF-CLI-22 (`DECISION ADDED A14`, `OfferTransmitted` read
   * as `OfferSent`): at `soumis`, once a bank has sent an offer, the card is
   * the drawing's « offres » state , the count from the door, « Comparez les
   * propositions et choisissez celle qui vous convient. », and « Voir mes
   * offres » greyed with its round (`L-2`): the buyer is told the truth and
   * never handed a control that leads nowhere.
   */
  const offers = dossier.offersReceived ?? 0;
  const offersState = dossier.stage === "soumis" && offers > 0;
  // `L-4`, M1 (A11): every offer received has ended (the clock's, or the closure's) and none is
  // retained , the card says so, in the buyer's words, and never « refusé ».
  const expiredState =
    dossier.stage === "soumis" &&
    offers === 0 &&
    (dossier.study?.offersEnded ?? 0) > 0 &&
    (dossier.retainedOffer ?? null) === null;
  // Round `L-3`, `M2` (WF-CLI-20): at `soumis` with no offer yet, the drawing's `banques` state; with
  // a document asked of HIM, `complement` , surfaced only when he must act (an internal return never
  // reaches him: the door answers no request for it). The counts are the door's, no bank is named.
  const study = dossier.study ?? null;
  const completion = dossier.completionRequest ?? null;
  const complementState = dossier.stage === "soumis" && !offersState && !expiredState && completion !== null;
  const banquesState =
    dossier.stage === "soumis" && !offersState && !expiredState && completion === null && study !== null;
  // Round `L-2`, `M4` (A8, WF-CLI-24 step 1): the file at « Offre » with the
  // buyer's choice made , the bank waits, the circuit is the bank's own.
  const retained = dossier.retainedOffer ?? null;
  // `CLIENT-OFFER-TERMS`: the four terms the door served on the retained offer, or none (an older answer).
  const offerTerms = retained?.terms ?? null;
  const acceptedState = dossier.stage === "offre" && retained !== null;
  // Round `T-1`, `M3` (WF-CLI-24 step 2, `P2-CLW-002`): « Prêt accordé, deux choses à faire » once the
  // bank's pack is sent inside the delegation , the door's `signature.packSentAt`; before it the
  // bank prepares (A8's sentence). The chip is the drawing's `vous`: two things are his.
  // The two the block reads, narrowed once: the retained offer and its signature, both present or neither.
  const signed =
    acceptedState &&
    retained.circuit === "signature" &&
    retained.signature !== null &&
    retained.signature.packSentAt !== null
      ? { retained, signature: retained.signature }
      : null;
  const signatureState = signed !== null;
  // Round `T-1b`, `M3` (WF-CLI-26, `P2-CLW-004`): the file at `conditions` , the grey card
  // « Dernières formalités », the five rows as the bank's facts and Hakili's, the money's circuit.
  const conditionsState = dossier.stage === "conditions" && retained !== null;
  const atConditions = dossier.stage === "conditions" && retained !== null ? { retained } : null;
  const bankConditions = dossier.conditions?.bank ?? null;
  const bankStateOf = (key: string): boolean =>
    bankConditions?.conditions.find((one) => one.key === key)?.state === "levee";
  // `T-1c`: the fee's fact by the door , financed by the bank (nothing to settle), or settled on evidence.
  const feeFinanced = dossier.conditions?.hakiliFeeFinanced === true;
  const feeSettled = dossier.conditions?.hakiliFeeSettled ?? null;
  const feeDeclared = props.fileFee?.state === "declare";
  const conditionRows: readonly {
    readonly id: string;
    readonly title: string;
    readonly who: string;
    readonly what: string;
    readonly done: boolean;
    readonly mine: boolean;
    /** The wait's reason in the row's title, where the fact is another round's (R81). */
    readonly note?: string;
  }[] =
    retained === null
      ? []
      : [
          // `T-1c`, `M2`: WF-CLI-26's first row split by record , the deposit (the bank's fifth
          // condition) and Hakili's fee (its own fact; absent when the bank finances it).
          {
            id: "apport",
            title: t("client.suivi.condApport"),
            who: t("client.suivi.condApportQui"),
            what: t("client.suivi.condApportQuoi"),
            done: bankStateOf("apport"),
            mine: !bankStateOf("apport"),
          },
          ...(feeFinanced
            ? []
            : [
                {
                  id: "frais",
                  title: t("client.suivi.condFrais"),
                  who: t("client.suivi.condFraisQui"),
                  what:
                    feeDeclared && feeSettled !== true
                      ? t("client.suivi.condFraisDeclareQuoi")
                      : t("client.suivi.condFraisQuoi"),
                  done: feeSettled === true,
                  mine: feeSettled !== true,
                },
              ]),
          {
            id: "domiciliation",
            title: t("client.suivi.condDomiciliation"),
            who: t("client.suivi.condDomiciliationQui"),
            what: t("client.suivi.condDomiciliationQuoi"),
            done: bankStateOf("domiciliation"),
            mine: false,
          },
          {
            id: "assurance-emprunteur",
            title: t("client.suivi.condAssuranceEmprunteur"),
            who: t("client.suivi.condAssuranceEmprunteurQui"),
            what: t("client.suivi.condAssuranceEmprunteurQuoi"),
            done: bankStateOf("assuranceEmprunteur"),
            mine: false,
          },
          {
            id: "assurance-auto",
            title: t("client.suivi.condAssuranceAuto"),
            // The « propre » wording, the only one while the insurance is parked (OD-40).
            who: t("client.suivi.condAssuranceAutoQui"),
            what: t("client.suivi.condAssuranceAutoQuoi"),
            done: bankStateOf("assuranceAuto"),
            mine: !bankStateOf("assuranceAuto"),
          },
          {
            id: "gage",
            title: t("client.suivi.condGage"),
            who: t("client.suivi.condGageQui"),
            what: t("client.suivi.condGageQuoi"),
            done: bankStateOf("gage"),
            mine: false,
          },
        ];
  const somethingMine = conditionRows.some((one) => !one.done && one.mine);
  const money = dossier.moneyFlow ?? null;
  const cardTone =
    offersState || complementState || signatureState
      ? TONES["vous"]
      : acceptedState
        ? TONES["attente"]
        : tone;
  // `M5`, WF-CLI-23's trigger: a financing file, an accepted offer, a warranty above zero the bank
  // did not finance, no decision recorded , and never on a cash file.
  const warrantyToDecide =
    dossier.intention !== "comptant" &&
    retained !== null &&
    retained.amountWarranty > 0 &&
    !retained.financesWarranty &&
    (dossier.warrantySettlement ?? null) === null
      ? retained
      : null;
  const trancher = props.trancherGarantie;
  const decideWarranty = (decision: "comptant" | "retiree"): void => {
    if (trancher === undefined) return;
    setBusy(true);
    setRefusal(null);
    void (async () => {
      try {
        await trancher(dossier.id, decision);
        if (relire !== undefined) await relire();
      } catch (failure) {
        setRefusal((failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"));
      } finally {
        setBusy(false);
      }
    })();
  };
  /**
   * `round C7`, `C7.2`, WF-CLI-19: *"the only client-facing copy that names a
   * person, and it names the chargé actually assigned"* , « <chargé> contrôle
   * vos pièces », at `analyse`, when the door answered a name. Without one the
   * generic sentence stands; no name is invented and no delay is promised.
   */
  const named =
    dossier.stage === "analyse" && typeof dossier.ownerName === "string" && dossier.ownerName !== ""
      ? `${dossier.ownerName} ${t("client.suivi.controleVosPieces")}`
      : null;
  /**
   * `round C3-A`, `C3-A.2`: **the control at `intake` is the DOOR's word.**
   *
   * `ACTIONS` still holds the stages whose gesture is a fixed destination;
   * `intake` is not one of them, because the same stage carries two different
   * gestures depending on money the screen must not read.
   */
  const geste = GESTES[dossier.geste ?? ""];
  const action = geste ?? ACTIONS.get(dossier.stage);
  const lancer = props.lancer;
  const relire = props.relire;
  const press = (): void => {
    if (action === undefined) return;
    if (action.path !== null) {
      props.naviguer(action.path);
      return;
    }
    // The launch: the door, then the re-read; a refusal in the door's own
    // sentence where the gesture is, as `C3-A.0(c)` renders it.
    if (lancer === undefined) return;
    setBusy(true);
    setRefusal(null);
    void (async () => {
      try {
        await lancer(dossier.id);
        if (relire !== undefined) await relire();
      } catch (failure) {
        setRefusal((failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"));
      } finally {
        setBusy(false);
      }
    })();
  };

  /**
   * `round W-13`, `M1`: **the verdict decides the card.** WF-CLI-12 (conforme:
   * the collecte's card, the fee acquired), WF-CLI-14 (écart de prix: the
   * refund promised before any decision, the expertised value as the
   * argument, the negotiation with the seller, the renouncement with a
   * refund) and WF-CLI-15 (non conforme: the refund, one control that
   * releases, no timeline). Every figure is the door's; the sentences are the
   * drawing's. The abandon is a DOOR pressed from the card, as the launch is.
   */
  const verdict = dossier.verdict;
  const closed = estEtapeClose(dossier.stage as EtapeDossier);
  const abandonner = props.abandonner;
  const renounce = (): void => {
    if (abandonner === undefined) return;
    setBusy(true);
    setRefusal(null);
    void (async () => {
      try {
        await abandonner(dossier.id);
        if (relire !== undefined) await relire();
      } catch (failure) {
        setRefusal((failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"));
      } finally {
        setBusy(false);
      }
    })();
  };
  const acceptProposedPrice = props.acceptProposedPrice;
  const acceptPrice = (): void => {
    if (acceptProposedPrice === undefined) return;
    setBusy(true);
    setRefusal(null);
    void (async () => {
      try {
        await acceptProposedPrice(dossier.id);
        if (relire !== undefined) await relire();
      } catch (failure) {
        setRefusal((failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"));
      } finally {
        setBusy(false);
      }
    })();
  };
  const fee = dossier.feeClientAmount === null ? null : formatMoney(dossier.feeClientAmount);
  const lireRapport = props.lireRapport;
  const voirLeRapport = (): void => {
    if (reading.open) {
      setReading({ ...reading, open: false });
      return;
    }
    if (lireRapport === undefined) return;
    void (async () => {
      try {
        const report = await lireRapport(dossier.id);
        setReading({ open: true, report, word: report === null ? t("client.suivi.rapportAbsent") : null });
      } catch (failure) {
        setReading({
          open: true,
          report: null,
          word: (failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"),
        });
      }
    })();
  };
  const readingBlock = !reading.open ? null : (
    <div className="mt-2 border-t border-gray-100 pt-2" data-testid="suivi-rapport-lecture">
      {reading.report === null ? (
        <p className="text-xs text-gray-500">{reading.word ?? t("client.suivi.rapportAbsent")}</p>
      ) : (
        <dl className="space-y-2 text-xs">
          {reading.report.current.document === null ? (
            // `LANE-E`, `M1`: the cabinet's paper is its right, not its obligation; no paper is said, not dashed.
            // With no sheet either, the one sentence below says so; this one would claim a stand-in that is not there.
            reading.report.current.sheet === null ? null : (
              <div>
                <dd className="text-gray-500" data-testid="suivi-rapport-papier-absent">
                  {fill("client.suivi.papierAbsent", { cabinet: reading.report.cabinetName })}
                </dd>
              </div>
            )
          ) : (
            <div>
              <dt className="text-gray-500">
                {fill("client.suivi.rapportDuCabinet", { cabinet: reading.report.cabinetName })}
              </dt>
              <dd className="mt-0.5 flex items-start justify-between gap-2">
                <span
                  className="break-all text-gray-500 tabular-nums"
                  data-testid="suivi-rapport-document-empreinte"
                >
                  {fill("client.suivi.rapportVersionEmpreinte", {
                    n: String(reading.report.current.version),
                    sha: reading.report.current.document.contentHash,
                  })}
                </span>
                <a
                  href={reading.report.current.document.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="suivi-rapport-document"
                  className="shrink-0 rounded-lg border border-indigo-100 bg-indigo-50 px-2.5 py-1 font-medium text-indigo-700"
                >
                  {t("client.suivi.papierOuvrir")}
                </a>
              </dd>
            </div>
          )}
          {reading.report.current.sheet === null ? (
            <div>
              <dt className="text-gray-500">{t("client.suivi.lectureHakili")}</dt>
              <dd className="mt-0.5 text-gray-500">
                {reading.report.current.document === null ? (
                  <span data-testid="suivi-rapport-rien">{t("client.suivi.rapportRien")}</span>
                ) : (
                  <span data-testid="suivi-rapport-lecture-absente">{t("client.suivi.lectureAbsente")}</span>
                )}
              </dd>
            </div>
          ) : (
            <div>
              <dt className="text-gray-500">{t("client.suivi.lectureHakili")}</dt>
              <dd className="mt-0.5 flex items-start justify-between gap-2">
                <span
                  className="break-all text-gray-500 tabular-nums"
                  data-testid="suivi-rapport-lecture-empreinte"
                >
                  {fill("client.suivi.rapportVersionEmpreinte", {
                    n: String(reading.report.current.version),
                    sha: reading.report.current.sheet.contentHash,
                  })}
                </span>
                <a
                  href={reading.report.current.sheet.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="suivi-rapport-sheet"
                  className="shrink-0 rounded-lg border border-gray-200 bg-white px-2.5 py-1 font-medium text-gray-700"
                >
                  {t("client.suivi.lectureOuvrir")}
                </a>
              </dd>
            </div>
          )}
        </dl>
      )}
    </div>
  );
  const reportCard =
    verdict === null || dossier.report === null ? null : (
      <div
        className="mt-3 rounded-2xl border border-gray-200 bg-white px-4 py-3.5"
        data-testid="suivi-rapport"
      >
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-gray-900">{t("client.suivi.rapportTitre")}</p>
          <span
            data-testid="suivi-rapport-chip"
            className={`rounded-full px-2 py-0.5 text-xs font-medium ${
              verdict === "conforme"
                ? "bg-emerald-50 text-emerald-700"
                : verdict === "ecartprix"
                  ? "bg-amber-50 text-amber-700"
                  : "bg-rose-50 text-rose-600"
            }`}
          >
            {verdict === "conforme"
              ? t("client.suivi.rapportChipConforme")
              : verdict === "ecartprix"
                ? t("client.suivi.rapportChipPrix")
                : t("client.suivi.rapportChipKo")}
          </span>
        </div>
        <dl className="mt-2 grid grid-cols-2 gap-y-1 text-xs">
          <dt className="text-gray-500">{t("client.suivi.rapportValeur")}</dt>
          <dd className="text-right font-medium text-gray-900">
            {formatMoney(dossier.report.estimatedValueAmount)}
          </dd>
          <dt className="text-gray-500">{t("client.suivi.rapportPrix")}</dt>
          <dd className="text-right font-medium text-gray-900">
            {formatMoney(dossier.report.askedPriceAmount)}
          </dd>
          <dt className="text-gray-500">{t("client.suivi.rapportEcart")}</dt>
          <dd className="text-right font-medium text-gray-900">
            {dossier.report.gapPct === null ? DASH : `${String(dossier.report.gapPct).replace(".", ",")} %`}
          </dd>
        </dl>
        {/* Round `C6` (`OD-31`, `OD-33`): « Voir le rapport » on every answered
            state , the cabinet's paper and Hakili's sheet, each with its
            fingerprint; the gap stays here, above them, never on them. */}
        <button
          type="button"
          data-testid="suivi-voir-rapport"
          disabled={lireRapport === undefined}
          title={lireRapport === undefined ? t("client.suivi.rapportAbsent") : undefined}
          onClick={voirLeRapport}
          className="mt-2 text-xs font-medium text-indigo-600 disabled:opacity-40"
        >
          {reading.open ? t("client.suivi.replierRapport") : t("client.suivi.voirRapport")}
        </button>
        {readingBlock}
      </div>
    );
  const refundOrdered =
    dossier.refund === null || fee === null ? null : (
      <p
        data-testid="suivi-remboursement-ordonne"
        className="mt-3 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-xs leading-relaxed text-gray-700"
      >
        {fill("client.suivi.remboursementOrdonne", {
          amount: fee,
          date: formatDate(new Date(dossier.refund.orderedOn)),
        })}
      </p>
    );

  /**
   * Round `L-3b`, `M3` (`P1-CLW-022`, WF-CLI-21, `DECISION ADDED 7`): **the one
   * client state with two copy variants.** Variant A , `refuse`, the pre-filter,
   * no bank ever saw the file , keeps the drawing's words; variant B ,
   * `refuseBanque`, every bank refused , reads the record's own sentence
   * « Aucune des banques saisies n'a retenu votre dossier. » and names no bank,
   * the rose card retitled by M0 (c) (the drawing's title says the file could
   * not be transmitted, and this one was). The white card as drawn. « Voir des
   * véhicules dans votre capacité » releases the file; « Acheter comptant, sans
   * crédit » keeps the drawing's look, greyed with its reason and round (R81,
   * WF-CLI-28, phase 2). No timeline (I26), no « Où en est l'étude » on a
   * terminal file.
   */
  if (dossier.stage === "refuse" || dossier.stage === "refuseBanque") {
    const byBanks = dossier.stage === "refuseBanque";
    return (
      <div className="pb-20">
        <div className="px-5 pb-3 pt-5">
          <h1 className="text-xl font-semibold tracking-tight text-gray-900">{t("client.suivi.titre")}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {labelWithYear(dossier)} · {dossier.reference}
          </p>
        </div>
        <div className="mb-4 px-5">
          <div
            className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-4"
            data-testid="suivi-etat"
            data-variant={byBanks ? "B" : "A"}
          >
            <p className="text-xs font-medium text-rose-600" data-testid="suivi-chip">
              {t("client.suivi.refuseChip")}
            </p>
            <p className="mt-1 text-base font-semibold text-gray-900" data-testid="suivi-titre-etat">
              {t("client.suivi.refuseTitre")}
            </p>
            <p className="mt-1 text-sm leading-relaxed text-gray-600" data-testid="suivi-refuse-detail">
              {byBanks ? t("client.suivi.etapeRefuseBanque") : t("client.suivi.etapeRefuse")}
            </p>
            {/**
             * `FX-2a`, `M4`: **the third of the three the record owes.**
             *
             * `03` owes the buyer the file's final stage, the closure's MOTIVE and
             * the closure's DATE, and `FX-1b`'s `M2` drew all three , on the
             * generic state card. **This branch returns a page of its own before
             * that card is reached**, so a file every bank refused , a CLOSED file
             * , showed the stage in its chip, the motive in the sentence above,
             * and no date at all. Booked by `FX-1b`'s verdict and cured here
             * rather than by moving the branch, which would put one card's copy
             * variants into the other's ladder.
             *
             * The motive is deliberately NOT `closure.code`: that stays
             * `sansSuite`-only by `FX-1b`'s own cure, because the code the door
             * records there is the buyer's change of mind. On a bank's refusal the
             * motive is the record's own sentence, already drawn above.
             *
             * `R81`: nothing where the door sent nothing , never an invented day.
             */}
            {(dossier.closure?.at ?? null) === null ? null : (
              <p className="mt-1 text-xs text-gray-500" data-testid="suivi-clos-le">
                {fill("client.suivi.closLe", {
                  date: formatDate(new Date(dossier.closure?.at ?? "")),
                })}
              </p>
            )}
          </div>
          <div
            className="mt-3 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-4"
            data-testid="suivi-refuse-rose"
          >
            <p className="text-sm font-semibold text-rose-700">
              {byBanks ? t("client.suivi.refuseRoseTitreB") : t("client.suivi.refuseRoseTitreA")}
            </p>
            <p className="mt-1.5 text-xs leading-relaxed text-gray-700">
              {byBanks ? t("client.suivi.refuseRoseDetailB") : t("client.suivi.refuseRoseDetailA")}
            </p>
          </div>
          <div
            className="mt-3 rounded-2xl border border-gray-200 bg-white px-4 py-3.5"
            data-testid="suivi-refuse-frais"
          >
            <p className="text-xs leading-relaxed text-gray-500">{t("client.suivi.refuseFrais")}</p>
          </div>
          <button
            type="button"
            data-testid="suivi-refuse-voir-vehicules"
            disabled={props.liberer === undefined}
            onClick={props.liberer}
            className="mt-3 w-full rounded-2xl bg-indigo-600 py-3.5 text-sm font-medium text-white disabled:opacity-40"
          >
            {t("client.suivi.refuseVoirVehicules")}
          </button>
          <button
            type="button"
            disabled
            title={t("client.suivi.refuseComptantMotif")}
            data-testid="suivi-refuse-comptant"
            className="mt-2 w-full rounded-2xl border border-gray-200 bg-white py-3 text-sm font-medium text-gray-700 opacity-60"
          >
            {t("client.suivi.refuseComptant")}
          </button>
        </div>
      </div>
    );
  }

  if (verdict === "ecartprix" || verdict === "nonconforme") {
    const gap = verdict === "ecartprix";
    const value = dossier.report === null ? null : formatMoney(dossier.report.estimatedValueAmount);
    // `PRICE-GAP-DEALER`, M2: the seller's answer on the platform, and the threshold the file restarts at (value x 1,10,
    // the door's own figure; rounded here only when an older door sent none).
    const answer = gap ? (dossier.report?.priceAnswer ?? null) : null;
    const proposed = answer === "propose";
    const kept = answer === "maintenu";
    // Rounded ONCE, by the verdict (9.2.3 M3): a door that sent no ceiling leaves the card on the expertised value.
    const ceiling =
      dossier.report?.ceilingAmount === undefined ? null : formatMoney(dossier.report.ceilingAmount);
    const standing = dossier.report === null ? null : formatMoney(dossier.report.askedPriceAmount);
    return (
      <div className="pb-20">
        <div className="px-5 pb-3 pt-5">
          <h1 className="text-xl font-semibold tracking-tight text-gray-900">{t("client.suivi.titre")}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {labelWithYear(dossier)} · {dossier.reference}
          </p>
        </div>
        <div className="mb-4 px-5">
          <div
            className={`rounded-2xl border px-4 py-4 ${gap ? "border-amber-200 bg-amber-50" : "border-rose-200 bg-rose-50"}`}
            data-testid="suivi-verdict"
            data-verdict={verdict}
          >
            {/* The drawing's `qui: "vous"` at an open verdict: the decision is his,
                whatever the stage's own chip says while the console has not moved it.
                A closed file keeps the door's word for its stage. */}
            <p
              className={`text-xs font-medium ${gap ? "text-amber-700" : "text-rose-600"}`}
              data-testid="suivi-chip"
            >
              {closed ? dossier.attente : CHIP_CLIENT.vous}
            </p>
            <p className="mt-1 text-base font-semibold text-gray-900">
              {gap
                ? proposed
                  ? t("client.suivi.verdictPropositionTitre")
                  : t("client.suivi.verdictPrixTitre")
                : t("client.suivi.verdictKoTitre")}
            </p>
            <p className="mt-1 text-sm leading-relaxed text-gray-600">
              {gap
                ? proposed
                  ? t("client.suivi.verdictPropositionDetail")
                  : t("client.suivi.verdictPrixDetail")
                : t("client.suivi.verdictKoDetail")}
            </p>
          </div>
          {reportCard}
          {dossier.refund === null && fee !== null ? (
            <div
              data-testid="suivi-remboursement"
              className="mt-3 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3.5 text-xs leading-relaxed text-gray-700"
            >
              <span className="font-medium">{fill("client.suivi.remboursementPromis", { amount: fee })}</span>{" "}
              {gap ? t("client.suivi.remboursementPrixMotif") : t("client.suivi.remboursementKoMotif")}{" "}
              {t("client.suivi.remboursementExecution")}
            </div>
          ) : null}
          {refundOrdered}
          {closed ? null : (
            <div
              className="mt-3 rounded-2xl border border-gray-200 bg-white px-4 py-4"
              data-testid="suivi-decision"
            >
              <p className="mb-1 text-sm font-medium text-gray-900">{t("client.suivi.decisionTitre")}</p>
              {gap ? (
                // The seller's answer, ON the platform (WF-CLI-14, founder's ruling of 16 October): waiting, kept, or proposed.
                <p
                  className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm leading-relaxed text-gray-900"
                  data-testid="suivi-reponse-vendeur"
                >
                  {proposed && standing !== null && value !== null
                    ? fill("client.suivi.vendeurPropose", { prix: standing, valeur: value })
                    : kept && standing !== null
                      ? fill("client.suivi.vendeurMaintient", { prix: standing })
                      : t("client.suivi.vendeurAttente")}
                </p>
              ) : null}
              <p className="mb-3 text-sm leading-relaxed text-gray-600">
                {gap ? (answer === null ? t("client.suivi.decisionPrix") : "") : t("client.suivi.decisionKo")}
              </p>
              {proposed ? (
                <button
                  type="button"
                  data-testid="suivi-accepter-prix"
                  disabled={busy || acceptProposedPrice === undefined}
                  onClick={acceptPrice}
                  className="mb-2 w-full rounded-2xl bg-indigo-600 py-3.5 text-sm font-medium text-white disabled:opacity-40"
                >
                  {t("client.suivi.acceptProposedPrice")}
                </button>
              ) : null}
              {gap && value !== null ? (
                <>
                  <button
                    type="button"
                    data-testid="suivi-negocier"
                    onClick={() => {
                      setNegotiating(!negotiating);
                    }}
                    className={
                      answer === null
                        ? "w-full rounded-2xl bg-indigo-600 py-3.5 text-sm font-medium text-white"
                        : // Once the seller has answered on the platform the phone call is secondary (founder, 16 October).
                          "w-full rounded-2xl border border-gray-200 bg-white py-3 text-sm font-medium text-gray-700"
                    }
                  >
                    {fill("client.suivi.negocier", { value })}
                  </button>
                  {negotiating ? (
                    <div
                      data-testid="suivi-negociation"
                      className="mt-2 rounded-xl border border-indigo-100 bg-indigo-50 px-3.5 py-3"
                    >
                      <p className="text-xs leading-relaxed text-gray-700">
                        {fill("client.suivi.negociationExplication", {
                          seller: dossier.sellerName,
                          ceiling: ceiling ?? value,
                        })}
                      </p>
                      {/* WF-CLI-14 step 5: the negotiation with the report in hand. */}
                      <button
                        type="button"
                        data-testid="suivi-negociation-rapport"
                        disabled={lireRapport === undefined}
                        onClick={voirLeRapport}
                        className="mt-2 text-xs font-medium text-indigo-700 disabled:opacity-40"
                      >
                        {t("client.suivi.negociationRapport")}
                      </button>
                      {dossier.sellerPhone === null ? (
                        <p className="mt-2 text-xs font-medium text-indigo-700">
                          {fill("client.suivi.appelerVendeur", { seller: dossier.sellerName, number: DASH })}
                        </p>
                      ) : (
                        <a
                          href={`tel:${dossier.sellerPhone}`}
                          className="mt-2 flex w-full items-center justify-center rounded-xl border border-indigo-200 bg-white py-2.5 text-xs font-medium text-indigo-700"
                        >
                          {fill("client.suivi.appelerVendeur", {
                            seller: dossier.sellerName,
                            number: dossier.sellerPhone,
                          })}
                        </a>
                      )}
                    </div>
                  ) : null}
                </>
              ) : null}
              <button
                type="button"
                data-testid="suivi-renoncer"
                disabled={busy}
                onClick={renounce}
                className={`mt-2 w-full rounded-2xl py-3 text-sm font-medium disabled:opacity-40 ${
                  gap && !kept ? "text-gray-500" : "bg-indigo-600 text-white"
                }`}
              >
                {gap ? t("client.suivi.renoncerRembourse") : t("client.suivi.voirAutresVehicules")}
              </button>
              {refusal === null ? null : (
                <p role="alert" data-testid="suivi-refus" className="mt-2 text-sm text-rose-600">
                  {refusal}
                </p>
              )}
            </div>
          )}
        </div>
        {/* WF-CLI-15 §6: no milestone timeline on a non-conforming file. */}
        {gap ? <Parcours dossier={dossier} /> : null}
      </div>
    );
  }

  // Conforme: the collecte's card, as ordered , the file goes to the pièces.
  // `L-1b` (found at the pairs): the verdict's card stands while the file is at the
  // report's own stages , `expertise` (the verdict arrives before the stage moves) and
  // `collecte` (the pièces it asks for). Past them the STAGE speaks: a file at
  // `soumis` read « Compléter mon dossier » over the banks' study.
  const conforme =
    verdict === "conforme" && !closed && (dossier.stage === "expertise" || dossier.stage === "collecte");
  const cardSentence = conforme ? SENTENCES.get("collecte") : sentence;
  const cardAction = conforme ? ACTIONS.get("collecte") : action;
  const pressCard = conforme
    ? () => {
        props.naviguer(cardAction?.path ?? "/dossier");
      }
    : press;

  return (
    <div className="pb-20">
      <div className="px-5 pb-3 pt-5">
        <h1 className="text-xl font-semibold tracking-tight text-gray-900">{t("client.suivi.titre")}</h1>
        <p className="mt-1 text-sm text-gray-500">
          {/* The drawing's second line carries the amount financed beside the
              car. The door answers no amount, so the file's own reference
              stands in its place rather than a figure nobody sent. */}
          {labelWithYear(dossier)} · {dossier.reference}
        </p>
      </div>

      <div className="mb-4 px-5">
        {/* A7: the offers received are the buyer's move (« À vous de jouer »);
            A8: after his choice the bank's (« En attente, rien à faire »). Both
            read with the drawing's `qui`, over 2.4.1's own chip for the stage ,
            flagged in the addendum. */}
        <div className={`rounded-2xl border px-4 py-4 ${cardTone?.box ?? ""}`} data-testid="suivi-etat">
          {/**
           * `FX-1`, `M2` and `FX-1b`, `M2`: the founder's rule of 24 September , « Suivi » keeps
           * the last closed dossier readable with its final stage, **the closure's motive** and
           * **its date**, in that order. The motive is the row's own `closure_code`, said in the
           * buyer's words; the date is `closed_at`, which the door now answers on every closed
           * stage. Each is drawn only where the door answers it (`R81`): a file closed at
           * `refuse` carries a date and no motive, and shows the date alone.
           */}
          <p className={`text-xs font-medium ${cardTone?.text ?? ""}`} data-testid="suivi-chip">
            {offersState || complementState || signatureState
              ? CHIP_CLIENT.vous
              : acceptedState
                ? CHIP_CLIENT.attente
                : dossier.attente}
          </p>
          <p className="mt-1 text-base font-semibold text-gray-900" data-testid="suivi-titre-etat">
            {conforme
              ? t("client.suivi.verdictConformeTitre")
              : offersState
                ? fill(offers > 1 ? "client.suivi.offresRecuesPlural" : "client.suivi.offresRecues", {
                    n: String(offers),
                  })
                : expiredState
                  ? t("client.suivi.offresExpireesTitre")
                  : complementState
                    ? t("client.suivi.complementTitre")
                    : banquesState
                      ? t("client.suivi.banquesTitre")
                      : signatureState
                        ? t("client.suivi.signatureTitre")
                        : conditionsState
                          ? t("client.suivi.conditionsTitre")
                          : acceptedState
                            ? t("client.suivi.offreAccepteeTitre")
                            : dossier.etape}
          </p>
          {/**
           * `FX-1b`, `M2`: the motive and the date sit AFTER the stage, because the founder's
           * rule names the three in that order , « its final stage, the closure's motive and the
           * closure's date ». The first shot of this row put them above the chip, and the frame
           * is what showed it: the record's order is part of the record.
           */}
          {closed && MOTIF_CLIENT.has(dossier.closure?.code ?? "") ? (
            <p className="mt-1 text-xs text-gray-500" data-testid="suivi-clos-motif">
              {fill("client.suivi.closMotif", {
                motif: t(MOTIF_CLIENT.get(dossier.closure?.code ?? "") ?? ""),
              })}
            </p>
          ) : null}
          {closed && (dossier.closure?.at ?? null) !== null ? (
            <p className="mt-1 text-xs text-gray-500" data-testid="suivi-clos-le">
              {fill("client.suivi.closLe", {
                date: formatDate(new Date(dossier.closure?.at ?? "")),
              })}
            </p>
          ) : null}
          {/* A stage this build has no sentence for shows its TITLE and
              nothing else. The title is the record's own label, so the screen
              is thinner, never wrong. */}
          {named !== null ? (
            <p className="mt-1 text-sm leading-relaxed text-gray-600" data-testid="suivi-charge">
              {named}
            </p>
          ) : offersState ? (
            <p className="mt-1 text-sm leading-relaxed text-gray-600" data-testid="suivi-offres">
              {t("client.suivi.offresComparez")}
            </p>
          ) : expiredState ? (
            <p className="mt-1 text-sm leading-relaxed text-gray-600" data-testid="suivi-offres-expirees">
              {fill("client.suivi.offresExpireesDetail", {
                charge:
                  typeof dossier.ownerName === "string" && dossier.ownerName !== ""
                    ? dossier.ownerName
                    : DASH,
              })}
            </p>
          ) : complementState ? (
            <p className="mt-1 text-sm leading-relaxed text-gray-600" data-testid="suivi-complement">
              {t("client.suivi.complementDetail")}
            </p>
          ) : banquesState ? (
            <p className="mt-1 text-sm leading-relaxed text-gray-600" data-testid="suivi-banques">
              {fill(
                study.engaged === 1 ? "client.suivi.banquesDetail" : "client.suivi.banquesDetailPluriel",
                {
                  n: String(study.engaged),
                },
              )}
              {study.slaDays === null
                ? ""
                : ` ${fill("client.suivi.banquesDelai", { n: String(study.slaDays) })}`}
            </p>
          ) : conditionsState ? (
            // `T-1c` (T-1b's booking (2), A14): the sentence conditional on the fee's fact.
            <p className="mt-1 text-sm leading-relaxed text-gray-600" data-testid="suivi-conditions-detail">
              {somethingMine && !feeFinanced && feeSettled !== true
                ? t("client.suivi.conditionsDetailReste")
                : t("client.suivi.conditionsDetail")}
            </p>
          ) : signatureState ? (
            // A12 (the addendum of 18 September): the insurance at the client's charge, the attestation
            // in the bank's name, then one passage at the agency.
            <p className="mt-1 text-sm leading-relaxed text-gray-600" data-testid="suivi-signature-detail">
              {t("client.suivi.signatureDetail")}
            </p>
          ) : retained !== null && acceptedState ? (
            <p className="mt-1 text-sm leading-relaxed text-gray-600" data-testid="suivi-circuit">
              {/* A8: above the threshold the drawing's sentence, its date only once a
                  committee session exists (none does: the sentence ends at « comité. »);
                  inside the delegation the bank prepares the accord and the appointment;
                  no threshold in force: the bank names its circuit. */}
              {retained.circuit === "comite"
                ? t("client.suivi.circuitComite")
                : retained.circuit === "signature"
                  ? fill("client.suivi.circuitSignature", { bank: retained.bankName })
                  : fill("client.suivi.circuitInconnu", { bank: retained.bankName })}
            </p>
          ) : dossier.closure?.code === "vehiculeRetireParLeConcessionnaire" ? (
            /* `K-2` (resumed), `M4`: the seller's breach, in the record's own words (WF-CLI-16's
               neighbour, K-2's M0 (d)); the refund's two states from the door's facts. */
            <div data-testid="suivi-clos-manquement" className="mt-1 text-sm leading-relaxed text-gray-600">
              <p>
                {dossier.breachRefund === null
                  ? t("client.suivi.closManquementSansFrais")
                  : fill("client.suivi.closManquement", {
                      amount: formatMoney(dossier.breachRefund.totalAmount),
                    })}
              </p>
              {dossier.breachRefund !== null && dossier.breachRefund.confirmedOn !== null ? (
                <p data-testid="suivi-rembourse-le" className="mt-1 font-medium text-emerald-700">
                  {fill("client.suivi.rembourseLe", {
                    date: formatDate(new Date(dossier.breachRefund.confirmedOn)),
                  })}
                </p>
              ) : dossier.breachRefund !== null ? (
                <p data-testid="suivi-remboursement-du" className="mt-1 text-gray-500">
                  {t("client.suivi.remboursementDu")}
                </p>
              ) : null}
            </div>
          ) : dossier.fileFeeRefund !== null && dossier.fileFeeRefund !== undefined ? (
            /* `T-2b`, `M0 (f)` (`P2-PAY-037`): the file ended before the bank's funds , Hakili's file fee
               ordered back to the means it came from, then confirmed on evidence. */
            <div data-testid="suivi-clos-frais" className="mt-1 text-sm leading-relaxed text-gray-600">
              <p>
                {fill("client.suivi.closFraisRembourses", {
                  amount: formatMoney(dossier.fileFeeRefund.totalAmount),
                })}
              </p>
              {dossier.fileFeeRefund.confirmedOn !== null ? (
                <p data-testid="suivi-frais-rembourse-le" className="mt-1 font-medium text-emerald-700">
                  {fill("client.suivi.rembourseLe", {
                    date: formatDate(new Date(dossier.fileFeeRefund.confirmedOn)),
                  })}
                </p>
              ) : (
                <p data-testid="suivi-frais-remboursement-du" className="mt-1 text-gray-500">
                  {t("client.suivi.remboursementDu")}
                </p>
              )}
            </div>
          ) : cardSentence === undefined ? null : (
            <p className="mt-1 text-sm leading-relaxed text-gray-600">{t(cardSentence)}</p>
          )}
          {/* `K-1`, `M3 (d)`: the buyer's sentence while the move is his, and nothing when it is not. */}
          {typeof dossier.waitingOnYouDays === "number" ? (
            <p className="mt-2 text-sm leading-relaxed text-amber-700" data-testid="suivi-attente-depuis">
              {fill("client.suivi.attenteDepuis", { n: String(dossier.waitingOnYouDays) })}
            </p>
          ) : null}
          {complementState ? (
            // WF-CLI-20 step 3: « Fournir la pièce demandée » opens the dossier tab, where the block sits first.
            <button
              type="button"
              data-testid="suivi-fournir-piece"
              onClick={() => {
                props.naviguer("/dossier");
              }}
              className="mt-3 w-full rounded-2xl bg-indigo-600 py-3 text-sm font-medium text-white"
            >
              {t("client.suivi.complementFournir")}
            </button>
          ) : null}
          {offersState ? (
            // `L-2`, `M2`: the control is live , the offers screen is built.
            <button
              type="button"
              data-testid="suivi-voir-offres"
              onClick={() => {
                props.naviguer("/dossier/offres");
              }}
              className="mt-3 w-full rounded-2xl bg-indigo-600 py-3 text-sm font-medium text-white"
            >
              {t("client.suivi.voirOffres")}
            </button>
          ) : null}
          {/* `CLIENT-OFFER-TERMS` (D-1102-3, « Both see all now. »): the four terms of the offer he retained, as the bank's own record serves them; nothing when the answer carries none. */}
          {offerTerms !== null && (acceptedState || conditionsState) ? (
            <dl
              className="mt-3 grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2"
              data-testid="suivi-offre-termes"
            >
              {termRows(offerTerms).map((row) => (
                <div key={row.key} data-testid={`suivi-offre-terme-${row.key}`}>
                  <dt className="text-xs text-gray-500">{row.label}</dt>
                  <dd className="text-sm font-medium tabular-nums text-gray-900">{row.value}</dd>
                </div>
              ))}
            </dl>
          ) : null}
          {acceptedState || expiredState ? (
            // `M4`: the milestone link to the locked comparison , on the card, since
            // `P1-CLW-024`'s timeline is not on this main. `L-4` (A11): the expired
            // comparison stays readable by the same link.
            <button
              type="button"
              data-testid="suivi-revoir-offres"
              onClick={() => {
                props.naviguer("/dossier/offres");
              }}
              className="mt-3 text-xs font-medium text-indigo-600"
            >
              {expiredState ? t("client.suivi.revoirOffresExpirees") : t("client.suivi.revoirOffres")}
            </button>
          ) : null}
          {/* Round `PAY-P`, `M3`: the DATE of the control, from the coverage
              door, on the fee-received and the launched states , never a
              delay; the door's own words when no Thursday can be derived;
              nothing while the door has not answered (`R81`). */}
          {controlDate === null ? null : (
            <p className="mt-1 text-sm leading-relaxed text-gray-600" data-testid="suivi-controle-date">
              {controlDate}
            </p>
          )}
          {/* `round C5-2`, `M2`, decision D: the pass failed without a verdict,
              the cabinet's reason in the drawing's words; who bears it is nowhere
              a figure on this card. */}
          {dossier.controlPrevented === null ? null : (
            <p
              className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-700"
              data-testid="suivi-controle-empeche"
            >
              {fill("client.suivi.controleEmpeche", {
                reason: preventedWords(dossier.controlPrevented),
              })}
            </p>
          )}
          {cardAction === undefined ? null : (
            <button
              type="button"
              data-testid="suivi-action"
              disabled={busy}
              onClick={pressCard}
              className="mt-3 w-full rounded-2xl bg-indigo-600 py-3 text-sm font-medium text-white disabled:opacity-40"
            >
              {t(cardAction.key)}
            </button>
          )}
          {refusal === null ? null : (
            <p role="alert" data-testid="suivi-refus" className="mt-2 text-sm text-rose-600">
              {refusal}
            </p>
          )}
        </div>
        {/* Round `T-1`, `M3` (WF-CLI-24 steps 3 to 5, the drawing's signature block with the insurance
            parked by record): the notice, the appointment from `lending.SignatureAppointmentSet` (the
            chargé's presence a dash , no door says it, R81), the documents to sign as the bank's pack
            lists them, « Ce qu'il faut apporter » with the deposit at the accepted offer's real amount
            and every fee line naming its recipient, the footer. */}
        {signed !== null ? (
          <div data-testid="suivi-signature">
            <div
              className="mt-3 rounded-2xl border border-indigo-100 bg-indigo-50 px-4 py-3.5"
              data-testid="suivi-signature-notice"
            >
              <p className="text-sm font-semibold text-gray-900">{t("client.suivi.signatureNoticeTitre")}</p>
              <p className="mt-1 text-xs leading-relaxed text-gray-600">
                {t("client.suivi.signatureNotice")}
              </p>
            </div>
            <div
              className="mt-3 rounded-2xl border border-gray-200 bg-white px-4 py-4"
              data-testid="suivi-signature-rdv"
            >
              <p className="mb-1 text-sm font-semibold">{t("client.suivi.signatureRdvTitre")}</p>
              <p className="text-sm leading-relaxed text-gray-600" data-testid="suivi-signature-rdv-ligne">
                {signed.signature.appointment === null
                  ? t("client.suivi.signatureRdvAFixer")
                  : fill("client.suivi.signatureRdvLigne", {
                      agence: signed.signature.appointment.agence,
                      when: formatDateTime(new Date(signed.signature.appointment.when)),
                    })}{" "}
                {fill("client.suivi.signatureRdvCharge", { presence: DASH })}
              </p>
              <div className="mt-3 grid grid-cols-1 gap-2">
                <button
                  type="button"
                  data-testid="suivi-signature-documents"
                  onClick={() => {
                    setDocumentsOpen(!documentsOpen);
                  }}
                  className="rounded-xl border border-gray-200 bg-white py-2.5 text-xs font-medium text-gray-700"
                >
                  {t("client.suivi.signatureVoirDocuments")}
                </button>
              </div>
              {documentsOpen ? (
                <div className="mt-3 border-t border-gray-100 pt-2.5" data-testid="suivi-signature-liste">
                  {signed.signature.documents.map((document) => (
                    <div key={document.code} className="flex items-start gap-2.5 py-1">
                      <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-gray-300" />
                      <span className="text-xs leading-relaxed text-gray-600">{document.label}</span>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
            {/* `T-1c`, `M2` (WF-CLI-24 step 5 amended, the founder's decision of 18 September): Hakili's
                fee is paid to Hakili, by the published means, before the appointment , its own block,
                the buyer's declaration by his door, Hakili's confirmation on evidence; the line leaves
                « Ce qu'il faut apporter », where the bank's own fee stays when it is not financed. */}
            {props.fileFee !== null && props.fileFee !== undefined ? (
              <FileFeeBlock
                fee={props.fileFee}
                form={feeForm}
                setForm={setFeeForm}
                declarer={
                  props.declareFileFee === undefined
                    ? null
                    : async (means, reference) => {
                        await props.declareFileFee?.(dossier.id, means, reference);
                      }
                }
              />
            ) : null}
            <div
              className="mt-3 overflow-hidden rounded-2xl border border-gray-200 bg-white"
              data-testid="suivi-signature-apporter"
            >
              <div className="border-b border-gray-100 px-4 py-3">
                <p className="text-sm font-semibold">{t("client.suivi.apporterTitre")}</p>
                <p className="mt-0.5 text-xs leading-relaxed text-gray-500">
                  {t("client.suivi.apporterSousTitre")}
                </p>
              </div>
              <ul className="divide-y divide-gray-50">
                {[
                  {
                    id: "identite",
                    title: t("client.suivi.apporterIdentite"),
                    detail: t("client.suivi.apporterIdentiteDetail"),
                  },
                  {
                    id: "apport",
                    title: fill("client.suivi.apporterApport", {
                      amount:
                        signed.retained.depositAmount === null
                          ? DASH
                          : formatMoney(signed.retained.depositAmount),
                    }),
                    detail: t("client.suivi.apporterApportDetail"),
                  },
                  {
                    id: "assurance",
                    title: t("client.suivi.apporterAssurance"),
                    detail: t("client.suivi.apporterAssuranceDetail"),
                  },
                  ...(signed.retained.financesBankFee
                    ? []
                    : [
                        {
                          id: "frais-banque",
                          title: fill("client.suivi.apporterFraisBanque", {
                            amount: formatMoney(signed.retained.feeBankAmount),
                            bank: signed.retained.bankName,
                          }),
                          detail: t("client.suivi.apporterFraisDetail"),
                        },
                      ]),
                  // `T-1c`: Hakili's fee no longer among what he brings to the counter , it is the block
                  // above, paid to Hakili before the appointment (WF-CLI-24 step 5 amended; the drawing's
                  // line kept as text in the record).
                  ...(signed.retained.amountWarranty > 0 &&
                  !signed.retained.financesWarranty &&
                  dossier.warrantySettlement === "comptant"
                    ? [
                        {
                          id: "garantie",
                          title: fill("client.suivi.apporterGarantie", {
                            amount: formatMoney(signed.retained.amountWarranty),
                          }),
                          detail: t("client.suivi.apporterGarantieDetail"),
                        },
                      ]
                    : []),
                ].map((line) => (
                  <li
                    key={line.id}
                    className="flex items-start gap-2.5 px-4 py-3"
                    data-testid={`suivi-apporter-${line.id}`}
                  >
                    <span
                      className="mt-1 h-4 w-4 shrink-0 rounded-full border-2 border-gray-200"
                      aria-hidden="true"
                    />
                    <div>
                      <p className="text-sm leading-snug text-gray-700">{line.title}</p>
                      <p className="mt-0.5 text-xs leading-relaxed text-gray-500">{line.detail}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <p className="bg-gray-50 px-4 py-3 text-xs leading-relaxed text-gray-500">
                {t("client.suivi.apporterPied")}
              </p>
            </div>
          </div>
        ) : null}
        {/* Round `T-1b`, `M3` (WF-CLI-26 steps 2 to 5): « Ce qui reste avant le versement » , five rows,
            each a FACT from the bank's conditions (never a constant), who owes it and what it is in the
            drawing's words; then « Comment l'argent circule » by steps 4 and 5, which WIN over the
            drawing's « Versé au vendeur »: the two transfers the bank issues, by the door's one quote. */}
        {atConditions !== null ? (
          <div data-testid="suivi-conditions">
            <div
              className="mt-3 overflow-hidden rounded-2xl border border-gray-200 bg-white"
              data-testid="suivi-conditions-reste"
            >
              <div className="border-b border-gray-100 px-4 py-3">
                <p className="text-sm font-semibold">{t("client.suivi.condTitre")}</p>
                <p
                  className="mt-0.5 text-xs leading-relaxed text-gray-500"
                  data-testid="suivi-conditions-entete"
                >
                  {t("client.suivi.condEntete")}{" "}
                  {somethingMine ? t("client.suivi.condUnPointVous") : t("client.suivi.condRienVous")}
                </p>
              </div>
              <ul className="divide-y divide-gray-50">
                {conditionRows.map((row) => (
                  <li
                    key={row.id}
                    className="flex items-start gap-2.5 px-4 py-3"
                    data-testid={`suivi-cond-${row.id}`}
                    data-done={row.done ? "true" : "false"}
                    title={row.note}
                  >
                    {row.done ? (
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" aria-hidden="true" />
                    ) : (
                      <Clock className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" aria-hidden="true" />
                    )}
                    <div className="flex-1">
                      <p className="text-sm leading-snug text-gray-700">{row.title}</p>
                      <p className="mt-0.5 text-xs text-gray-400">{row.who}</p>
                      <p className="mt-1 text-xs leading-relaxed text-gray-500">{row.what}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div
              className="mt-3 rounded-2xl border border-gray-200 bg-white px-4 py-4"
              data-testid="suivi-argent"
            >
              <p className="mb-2 text-sm font-semibold">{t("client.suivi.argentTitre")}</p>
              {[
                [
                  "apport",
                  t("client.suivi.argentApport"),
                  atConditions.retained.depositAmount === null
                    ? DASH
                    : formatMoney(atConditions.retained.depositAmount),
                ],
                ["pret", t("client.suivi.argentPret"), formatMoney(atConditions.retained.amountVehicle)],
                ...(atConditions.retained.amountWarranty > 0 && atConditions.retained.financesWarranty
                  ? [
                      [
                        "garantie",
                        t("client.suivi.argentGarantieFinancee"),
                        formatMoney(atConditions.retained.amountWarranty),
                      ],
                    ]
                  : atConditions.retained.amountWarranty > 0 && dossier.warrantySettlement === "comptant"
                    ? [
                        [
                          "garantie",
                          t("client.suivi.argentGarantieComptant"),
                          formatMoney(atConditions.retained.amountWarranty),
                        ],
                      ]
                    : []),
                [
                  "vendeur",
                  fill("client.suivi.argentVendeur", {
                    rate: money === null ? DASH : String(100 - money.ratePct),
                  }),
                  money === null ? DASH : formatMoney(money.dealerAmount),
                ],
                [
                  "hakili",
                  fill("client.suivi.argentHakili", { rate: money === null ? DASH : String(money.ratePct) }),
                  money === null ? DASH : formatMoney(money.hakiliAmount),
                ],
                [
                  "frais-banque",
                  atConditions.retained.financesBankFee
                    ? t("client.suivi.argentFraisBanqueBanque")
                    : t("client.suivi.argentFraisBanqueVous"),
                  formatMoney(atConditions.retained.feeBankAmount),
                ],
                [
                  "frais-hakili",
                  atConditions.retained.financesHakiliFee
                    ? t("client.suivi.argentFraisHakiliBanque")
                    : t("client.suivi.argentFraisHakiliVous"),
                  formatMoney(atConditions.retained.feeHakiliAmount),
                ],
                // The total of the two fees, only when the client settles both (the record allows it).
                ...(!atConditions.retained.financesBankFee && !atConditions.retained.financesHakiliFee
                  ? [
                      [
                        "frais-total",
                        t("client.suivi.argentFraisTotal"),
                        formatMoney(
                          atConditions.retained.feeBankAmount + atConditions.retained.feeHakiliAmount,
                        ),
                      ],
                    ]
                  : []),
              ].map(([id = "", label = "", value = ""]) => (
                <div
                  key={id}
                  className="flex items-center justify-between py-1"
                  data-testid={`suivi-argent-${id}`}
                >
                  <span className="text-xs text-gray-500">{label}</span>
                  <span className="text-sm font-medium tabular-nums">{value}</span>
                </div>
              ))}
              <p className="mt-2 text-xs leading-relaxed text-gray-500" data-testid="suivi-argent-phrase">
                {t("client.suivi.argentPhrase")}
              </p>
              <p className="mt-2 text-xs leading-relaxed text-gray-400">
                {atConditions.retained.financesBankFee && atConditions.retained.financesHakiliFee
                  ? t("client.suivi.argentPiedFinances")
                  : t("client.suivi.argentPiedVous")}
              </p>
            </div>
          </div>
        ) : null}
        {/* `T-2` (WF-CLI-27, `P2-CLW-005`'s neighbour): the money's circuit read from the two legs once the bank
            ordered , the vendor's part confirmed by him, Hakili's received on its account , and the end of the
            journey in the record's words; the bon's screen by its own link, or the block's reason. */}
        {props.voucher !== null &&
        props.voucher !== undefined &&
        (dossier.stage === "decaisse" || dossier.stage === "conditions") ? (
          <div
            className="mt-3 rounded-2xl border border-gray-200 bg-white px-4 py-4"
            data-testid="suivi-argent-circuit"
          >
            <p className="mb-1 text-sm font-semibold">{t("client.suivi.argentTitre")}</p>
            <p className="mb-2 text-xs leading-relaxed text-gray-500">{t("client.suivi.circuitMots")}</p>
            <ul className="space-y-1 text-sm text-gray-700">
              <li className="flex justify-between gap-3" data-testid="suivi-circuit-vendeur">
                <span>{t("client.suivi.circuitVendeur")}</span>
                <span className="text-right text-gray-600">
                  {props.voucher.legs.dealerConfirmedAt === null
                    ? t("client.suivi.argentAttente")
                    : fill("client.suivi.argentConfirme", {
                        date: formatDate(new Date(props.voucher.legs.dealerConfirmedAt)),
                      })}
                </span>
              </li>
              <li className="flex justify-between gap-3" data-testid="suivi-circuit-hakili">
                <span>{t("client.suivi.circuitHakili")}</span>
                <span className="text-right text-gray-600">
                  {props.voucher.legs.hakiliReceivedAt === null
                    ? t("client.suivi.argentAttente")
                    : fill("client.suivi.argentRecue", {
                        date: formatDate(new Date(props.voucher.legs.hakiliReceivedAt)),
                      })}
                </span>
              </li>
            </ul>
            {props.voucher.voucher === null ? (
              <p className="mt-2 text-xs leading-relaxed text-amber-700" data-testid="suivi-circuit-bloque">
                {props.voucher.blocked?.reason ?? ""}
              </p>
            ) : (
              <p className="mt-2 text-xs leading-relaxed text-emerald-700" data-testid="suivi-circuit-fin">
                {t("client.suivi.argentFin")}
              </p>
            )}
            <button
              type="button"
              onClick={() => {
                props.naviguer("/dossier/retrait");
              }}
              className="mt-3 w-full rounded-2xl bg-indigo-600 py-3 text-sm font-medium text-white"
              data-testid="suivi-voir-retrait"
            >
              {t("client.suivi.voirRetrait")}
            </button>
          </div>
        ) : null}
        {/* Round `L-3`, `M2` (WF-CLI-20 step 2): « Où en est l'étude », the four rows from the summary
          door , « Pièce complémentaire demandée » only in the complement state , and the footer. */}
        {(banquesState || complementState) && study !== null ? (
          <div
            className="mt-3 rounded-2xl border border-gray-200 bg-white px-4 py-4"
            data-testid="suivi-etude"
          >
            <p className="mb-2 text-sm font-semibold text-gray-900">{t("client.suivi.etudeTitre")}</p>
            {(
              [
                ["client.suivi.etudeBanques", String(study.engaged)],
                ["client.suivi.etudeEnCours", String(study.studying)],
                ...(complementState
                  ? [["client.suivi.etudeComplement", String(study.completionsRequested)] as const]
                  : []),
                [
                  "client.suivi.etudeOffres",
                  study.offersReceived === 0
                    ? t("client.suivi.etudeOffresAucune")
                    : String(study.offersReceived),
                ],
              ] as const
            ).map(([key, value]) => (
              <div key={key} className="flex items-center justify-between py-1.5">
                <span className="text-sm text-gray-500">{t(key)}</span>
                <span className="text-sm font-medium tabular-nums text-gray-700">{value}</span>
              </div>
            ))}
            <p className="mt-2 text-xs leading-relaxed text-gray-500">{t("client.suivi.etudePied")}</p>
          </div>
        ) : null}
        {/* WF-CLI-23: the bank granted the loan without the warranty , the buyer decides here,
            without touching the loan; the card disappears the moment a decision is recorded. */}
        {warrantyToDecide !== null ? (
          <div
            className="mt-3 rounded-2xl border border-indigo-200 bg-white px-4 py-4"
            data-testid="suivi-garantie"
          >
            <p className="text-sm font-semibold text-gray-900">{t("client.suivi.garantieTitre")}</p>
            <p className="mt-1.5 text-xs leading-relaxed text-gray-600">
              {fill("client.suivi.garantieDetail", {
                bank: warrantyToDecide.bankName,
                amount: formatMoney(warrantyToDecide.amountWarranty),
              })}
            </p>
            <button
              type="button"
              data-testid="suivi-garantie-comptant"
              disabled={busy || trancher === undefined}
              onClick={() => {
                decideWarranty("comptant");
              }}
              className="mt-3 w-full rounded-2xl bg-indigo-600 py-3 text-sm font-medium text-white disabled:opacity-40"
            >
              {t("client.suivi.garantieComptant")}
            </button>
            <button
              type="button"
              data-testid="suivi-garantie-retirer"
              disabled={busy || trancher === undefined}
              onClick={() => {
                decideWarranty("retiree");
              }}
              className="mt-2 w-full rounded-2xl border border-gray-200 bg-white py-3 text-sm font-medium text-gray-700 disabled:opacity-40"
            >
              {t("client.suivi.garantieRetirer")}
            </button>
          </div>
        ) : null}
        {verdict === "conforme" ? reportCard : null}
        {/* `T-2b`, `M2 (a)` (WF-CLI-13): the cash buyer's three options on the conforming report , financing
            (the eligibility road, the intent NOT flipped here), cash (the settlement opened by his own door),
            renouncement (WF-CLI-16's door); once the option is taken, the conclusion's link. */}
        {verdict === "conforme" &&
        dossier.intention === "comptant" &&
        !closed &&
        props.comptantOuvert !== true ? (
          <div className="mt-3 space-y-2" data-testid="suivi-comptant-options">
            <button
              type="button"
              data-testid="suivi-comptant-financement"
              onClick={() => {
                props.naviguer("/eligibilite");
              }}
              className="w-full rounded-2xl border border-indigo-200 bg-white py-3 text-sm font-medium text-indigo-600"
            >
              {t("client.suivi.optionFinancement")}
            </button>
            <button
              type="button"
              data-testid="suivi-comptant-acheter"
              disabled={busy || props.acheterComptant === undefined}
              onClick={() => {
                const open = props.acheterComptant;
                if (open === undefined) return;
                setBusy(true);
                setRefusal(null);
                void open(dossier.id)
                  .then(() => {
                    props.naviguer("/dossier/comptant");
                  })
                  .catch((failure: unknown) => {
                    setRefusal(
                      (failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"),
                    );
                  })
                  .finally(() => {
                    setBusy(false);
                  });
              }}
              className="w-full rounded-2xl bg-indigo-600 py-3 text-sm font-medium text-white disabled:opacity-40"
            >
              {t("client.suivi.optionComptant")}
            </button>
            <button
              type="button"
              data-testid="suivi-comptant-renoncer"
              disabled={busy || abandonner === undefined}
              onClick={renounce}
              className="w-full rounded-2xl border border-gray-200 bg-white py-3 text-sm font-medium text-gray-700 disabled:opacity-40"
            >
              {t("client.suivi.optionRenoncer")}
            </button>
          </div>
        ) : null}
        {/* `T-2c`, `M2 (4)` (WF-CLI-28 step 4, 02 §2.4.1's comptant end): the keys passed, the sale is
            concluded , the sentence, the bon's link, and « Chercher un autre véhicule », which releases
            the dossier: a concluded file is no live file (I25), the search is open again. */}
        {dossier.intention === "comptant" && typeof props.comptantRemisLe === "string" ? (
          <div className="mt-3 rounded-2xl bg-emerald-50 p-4" data-testid="suivi-comptant-fin">
            <p className="text-sm font-semibold text-emerald-800" data-testid="suivi-comptant-fin-mots">
              {fill("client.suivi.comptantConclu", { date: formatDate(new Date(props.comptantRemisLe)) })}
            </p>
            <button
              type="button"
              data-testid="suivi-comptant-autre"
              onClick={() => {
                props.naviguer("/recherche");
              }}
              className="mt-3 w-full rounded-2xl bg-emerald-700 py-3 text-sm font-medium text-white"
            >
              {t("client.suivi.chercherAutre")}
            </button>
          </div>
        ) : null}
        {dossier.intention === "comptant" && props.comptantOuvert === true ? (
          <button
            type="button"
            data-testid="suivi-comptant-voir"
            onClick={() => {
              props.naviguer("/dossier/comptant");
            }}
            className="mt-3 w-full rounded-2xl bg-indigo-600 py-3 text-sm font-medium text-white"
          >
            {t("client.suivi.voirComptant")}
          </button>
        ) : null}
        {/* `K-2` (resumed): « les frais restent acquis » is not said on a file the seller's breach
            closed , the fees are refunded at the dealer's charge, the closure card says so. */}
        {verdict === "conforme" && dossier.closure?.code !== "vehiculeRetireParLeConcessionnaire" ? (
          <p className="mt-3 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3.5 text-xs leading-relaxed text-gray-700">
            {t("client.suivi.verdictConformeFrais")}
          </p>
        ) : null}
        {refundOrdered}
      </div>

      {/**
       * `STATUS-FIRST` (the A-H architect's ruling of 2 November): the renouncement stands AFTER the status card, in the page's own column, with the kit's gap; a destructive action is never the page's first control.
       *
       * `FX-1`, `M3` (`F8`, the staging sitting of 20 to 22 September) , **the buyer may leave.**
       *
       * The sitting found a file at Prise en charge with nothing paid and no door out. The SERVER
       * already allowed it , `TR-1` pressed `POST /me/dossiers/{id}/abandon` and the file became
       * `sansSuite` , and `02`:430 governs: closure from ANY non-terminal stage, the motive
       * mandatory, and **no payment cut-off** (the architect's reading of one was struck by his own
       * ruling on the triage). Only this screen withheld it, offering the renouncement to a CASH
       * buyer with a conforming report and to nobody else.
       *
       * The motive is NAMED and not chosen: the door records `clientChangeAvis`, and a picker
       * whose other answers the door discarded would be a question asked in bad faith. The label
       * is the enum's own.
       */}
      {closed || abandonner === undefined ? null : (
        <div className="mb-4 px-5">
          <div
            className="rounded-2xl border border-gray-200 bg-white px-4 py-3"
            data-testid="suivi-renoncement"
          >
            <p className="text-sm font-medium text-gray-900">{t("client.suivi.renoncement")}</p>
            <p className="mt-1 text-xs leading-relaxed text-gray-500">{t("client.suivi.renoncementMotif")}</p>
            <button
              type="button"
              data-testid="suivi-renoncement-confirmer"
              disabled={busy}
              onClick={renounce}
              className="mt-2 w-full rounded-2xl border border-gray-200 py-2.5 text-sm font-medium text-gray-700 disabled:opacity-40"
            >
              {t("client.suivi.renoncementConfirmer")}
            </button>
          </div>
        </div>
      )}

      <Parcours dossier={dossier} />
    </div>
  );
}

/** The drawing's `RAISONS_EMPECHEMENT`, in the buyer's words; a reason this build has not met prints as itself. */
const PREVENTED_WORDS: ReadonlyMap<string, string> = new Map([
  ["absent", "client.suivi.empecheAbsent"],
  ["cles", "client.suivi.empecheCles"],
  ["batterie", "client.suivi.empecheBatterie"],
  ["inaccessible", "client.suivi.empecheInaccessible"],
  ["ferme", "client.suivi.empecheFerme"],
  ["autre", "client.suivi.empecheAutre"],
]);
const preventedWords = (prevented: { readonly reason: string; readonly detail: string | null }): string => {
  const key = PREVENTED_WORDS.get(prevented.reason);
  const words = key === undefined ? prevented.reason : t(key);
  return prevented.detail === null ? words : `${words} : ${prevented.detail}`;
};

/** The path travelled (`I20`), the drawing's timeline. */
function Parcours({ dossier }: { readonly dossier: SuiviDossier }): React.JSX.Element {
  // A closed file is nowhere « en cours »: its last step is reached, not running.
  const closed = estEtapeClose(dossier.stage as EtapeDossier);
  return (
    <div className="px-5">
      <div className="rounded-2xl border border-gray-200 bg-white px-4 py-4" data-testid="suivi-parcours">
        {dossier.chemin.map((step, index) => {
          const encours = step.id === dossier.stage;
          const dernier = index === dossier.chemin.length - 1;
          return (
            <div key={step.id} className="flex gap-3">
              <div className="flex flex-col items-center">
                <div
                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-indigo-600 ${
                    encours ? "ring-4 ring-indigo-100" : ""
                  }`}
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-white" />
                </div>
                {dernier ? null : <div className="my-1 w-px flex-1 bg-indigo-200" />}
              </div>
              <div className={dernier ? "pb-0" : "pb-4"}>
                <p
                  className={`text-sm ${
                    encours ? "font-semibold text-gray-900" : "font-medium text-gray-700"
                  }`}
                >
                  {step.label}
                  {encours && !closed ? (
                    <span className="text-xs font-normal text-indigo-600"> {t("client.suivi.enCours")}</span>
                  ) : null}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * `T-1c`, `M2` (WF-CLI-24 step 5 amended; `P2-CLW-004`'s neighbour): **« Frais de dossier
 * Hakili » on the signature card.** The amount and the recipient as the door answers them; the
 * published means (the account beside the transfer); the state , à régler, déclarés, encaissés,
 * financés par la banque; « J'ai réglé les frais de dossier », a form with the means used and
 * the reference, the door's refusal in its own words. Nothing confirmed here (round 25).
 */
function FileFeeBlock(props: {
  readonly fee: FileFeeView;
  readonly form: { open: boolean; means: string; reference: string; word: string | null };
  readonly setForm: (form: { open: boolean; means: string; reference: string; word: string | null }) => void;
  readonly declarer: ((means: string, reference: string) => Promise<void>) | null;
}): React.JSX.Element {
  const { fee, form } = props;
  const meanLabel = (code: string): string => (isPaymentMean(code) ? PAYMENT_MEAN_LABELS[code] : code);
  const due = fee.state === "aRegler";
  const stateWords =
    fee.state === "encaisse"
      ? t("client.suivi.fraisEtatEncaisse")
      : fee.state === "declare"
        ? t("client.suivi.fraisEtatDeclare")
        : fee.state === "financeParLaBanque"
          ? t("client.suivi.fraisEtatFinances")
          : t("client.suivi.fraisEtatARegler");
  return (
    <div
      className="mt-3 overflow-hidden rounded-2xl border border-gray-200 bg-white"
      data-testid="suivi-frais"
      data-state={fee.state}
    >
      <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-4 py-3">
        <div>
          <p className="text-sm font-semibold">{t("client.suivi.fraisTitre")}</p>
          <p className="mt-0.5 text-xs leading-relaxed text-gray-500" data-testid="suivi-frais-montant">
            {fee.state === "financeParLaBanque"
              ? fill("client.suivi.fraisFinances", { amount: formatMoney(fee.hakiliAmount) })
              : fill("client.suivi.fraisMontant", { amount: formatMoney(fee.hakiliAmount) })}
          </p>
        </div>
        <span
          data-testid="suivi-frais-etat"
          className={
            "shrink-0 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium " +
            (fee.state === "encaisse" || fee.state === "financeParLaBanque"
              ? "bg-emerald-50 text-emerald-700"
              : fee.state === "declare"
                ? "bg-indigo-50 text-indigo-700"
                : "bg-amber-50 text-amber-700")
          }
        >
          {stateWords}
        </span>
      </div>
      {due || fee.state === "declare" ? (
        <div className="px-4 py-3" data-testid="suivi-frais-moyens">
          <p className="text-xs font-medium text-gray-700">{t("client.suivi.fraisMoyensTitre")}</p>
          <ul className="mt-1.5 space-y-1">
            {fee.means.map((mean) => (
              <li
                key={mean.code}
                className="text-xs leading-relaxed text-gray-600"
                data-testid={`suivi-frais-moyen-${mean.code}`}
              >
                {meanLabel(mean.code)}
                {mean.account === null
                  ? ""
                  : ` · ${fill("client.suivi.fraisVirementCompte", {
                      bank: mean.account.bankName,
                      holder: mean.account.accountName,
                      number: mean.account.accountNumber,
                    })}`}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {fee.state === "declare" && fee.declaration !== null ? (
        <p
          className="border-t border-gray-100 px-4 py-3 text-xs leading-relaxed text-gray-600"
          data-testid="suivi-frais-declare"
        >
          {fill("client.suivi.fraisDeclare", {
            date: formatDate(new Date(fee.declaration.at)),
            means: meanLabel(fee.declaration.means),
            reference: fee.declaration.reference,
          })}
        </p>
      ) : null}
      {fee.state === "encaisse" && fee.collectedAt !== null ? (
        <p
          className="border-t border-gray-100 px-4 py-3 text-xs leading-relaxed text-gray-600"
          data-testid="suivi-frais-encaisse"
        >
          {fill("client.suivi.fraisEncaisse", { date: formatDate(new Date(fee.collectedAt)) })}
        </p>
      ) : null}
      {due && props.declarer !== null ? (
        <div className="border-t border-gray-100 px-4 py-3">
          {form.open ? (
            <form
              data-testid="suivi-frais-formulaire"
              onSubmit={(event) => {
                event.preventDefault();
                const declarer = props.declarer;
                if (declarer === null) return;
                void declarer(form.means, form.reference)
                  .then(() => {
                    props.setForm({ open: false, means: "", reference: "", word: null });
                  })
                  .catch((error: unknown) => {
                    props.setForm({
                      ...form,
                      word: error instanceof Error ? error.message : t("common.state.erreur"),
                    });
                  });
              }}
            >
              <p className="text-sm font-medium text-gray-700">{t("client.suivi.fraisDeclarationTitre")}</p>
              <label className="mt-2 block text-xs text-gray-600" htmlFor="suivi-frais-moyen">
                {t("client.suivi.fraisDeclarationMoyen")}
              </label>
              <select
                id="suivi-frais-moyen"
                data-testid="suivi-frais-moyen"
                required
                value={form.means}
                onChange={(event) => {
                  props.setForm({ ...form, means: event.target.value });
                }}
                className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
              >
                <option value="">{DASH}</option>
                {fee.means.map((mean) => (
                  <option key={mean.code} value={mean.code}>
                    {meanLabel(mean.code)}
                  </option>
                ))}
              </select>
              <label className="mt-2 block text-xs text-gray-600" htmlFor="suivi-frais-reference">
                {t("client.suivi.fraisDeclarationReference")}
              </label>
              <input
                id="suivi-frais-reference"
                data-testid="suivi-frais-reference"
                required
                maxLength={80}
                value={form.reference}
                onChange={(event) => {
                  props.setForm({ ...form, reference: event.target.value });
                }}
                className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
              />
              <p className="mt-1 text-xs text-gray-400">{t("client.suivi.fraisDeclarationReferenceAide")}</p>
              {form.word === null ? null : (
                <p role="alert" data-testid="suivi-frais-refus" className="mt-2 text-xs text-rose-600">
                  {form.word}
                </p>
              )}
              <div className="mt-3 flex gap-2">
                <button
                  type="submit"
                  data-testid="suivi-frais-envoyer"
                  className="flex-1 rounded-xl bg-indigo-600 py-2.5 text-sm font-medium text-white"
                >
                  {t("client.suivi.fraisDeclarationEnvoyer")}
                </button>
                <button
                  type="button"
                  data-testid="suivi-frais-annuler"
                  onClick={() => {
                    props.setForm({ open: false, means: "", reference: "", word: null });
                  }}
                  className="flex-1 rounded-xl border border-gray-200 bg-white py-2.5 text-sm font-medium text-gray-700"
                >
                  {t("client.suivi.fraisDeclarationAnnuler")}
                </button>
              </div>
            </form>
          ) : (
            <button
              type="button"
              data-testid="suivi-frais-declarer"
              onClick={() => {
                props.setForm({ open: true, means: fee.means[0]?.code ?? "", reference: "", word: null });
              }}
              className="w-full rounded-xl bg-indigo-600 py-2.5 text-sm font-medium text-white"
            >
              {t("client.suivi.fraisDeclarer")}
            </button>
          )}
        </div>
      ) : null}
      <p className="bg-gray-50 px-4 py-3 text-xs leading-relaxed text-gray-500">
        {t("client.suivi.fraisPied")}
      </p>
    </div>
  );
}
